import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './TrackOrder.css';

/* ── Types ───────────────────────────────────────── */
interface TrackingStep {
  label: string;
  detail: string;
  time: string;
  done: boolean;
  active: boolean;
}

interface OrderInfo {
  id: string;
  status: string;
  product: string;
  qty: number;
  total: string;
  payment: string;
  address: string;
  estimatedDate: string;
  steps: TrackingStep[];
}

/* ── Mock data ───────────────────────────────────── */
const MOCK_ORDER: OrderInfo = {
  id: 'IB-98234-X',
  status: 'Out for Delivery',
  product: 'CP PLUS 2MP Full HD IR Dome CCTV Camera',
  qty: 2,
  total: '₹3,710',
  payment: 'Prepaid — UPI',
  address: '42, Sector 18, Gurugram, Haryana — 122015',
  estimatedDate: 'Today by 8:00 PM',
  steps: [
    { label: 'Order Placed', detail: 'Your order was successfully placed.', time: '10 May 2026, 02:30 PM', done: true, active: false },
    { label: 'Payment Confirmed', detail: 'Payment received via UPI.', time: '10 May 2026, 03:15 PM', done: true, active: false },
    { label: 'Packed at Warehouse', detail: 'Items verified, quality checked & packed.', time: '11 May 2026, 09:00 AM', done: true, active: false },
    { label: 'Shipped', detail: 'Handed over to courier — DTDC Express.', time: '11 May 2026, 04:30 PM', done: true, active: false },
    { label: 'Out for Delivery', detail: 'Your package is with the delivery agent.', time: 'Today, 10:00 AM', done: false, active: true },
    { label: 'Delivered', detail: 'Estimated delivery by 8:00 PM today.', time: 'Pending', done: false, active: false },
  ],
};

/* ── Component ───────────────────────────────────── */
const TrackOrder: React.FC = () => {
  const { orderId: paramId } = useParams<{ orderId?: string }>();
  const [orderId, setOrderId] = useState(paramId !== 'search' ? (paramId || '') : '');
  const [phone, setPhone] = useState('');
  const [tracked, setTracked] = useState(!!paramId && paramId !== 'search');
  const [order] = useState<OrderInfo>(MOCK_ORDER);
  const [error, setError] = useState('');

  const handleTrack = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!orderId.trim() || phone.trim().length < 10) {
      setError('Please enter a valid Order ID and 10-digit mobile number.');
      return;
    }
    setTracked(true);
  };

  const progress = ((order.steps.filter(s => s.done).length) / order.steps.length) * 100;

  return (
    <div className="to-page">

      {/* ── Breadcrumb ── */}
      <div className="to-breadcrumb-bar">
        <div className="to-breadcrumb">
          <Link to="/">Home</Link><span>›</span>
          <span className="to-breadcrumb__current">Track Order</span>
        </div>
      </div>

      <div className="to-container">

        {/* ── Page title ── */}
        <div className="to-page-header">
          <div className="to-page-header__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4ABCE8" strokeWidth="1.8">
              <rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/>
              <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
          </div>
          <div>
            <h1 className="to-page-header__title">Track Your Order</h1>
            <p className="to-page-header__sub">Get real-time updates on your shipment status.</p>
          </div>
        </div>

        <div className="to-layout">

          {/* ── LEFT: Search form ── */}
          <div className="to-left">
            <div className="to-card">
              <h2 className="to-card__title">Enter Order Details</h2>
              <p className="to-card__sub">Enter your Order ID and registered mobile number to track your shipment.</p>

              <form onSubmit={handleTrack} className="to-form">
                <div className="to-form__field">
                  <label className="to-form__label">Order ID</label>
                  <div className="to-form__input-wrap">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    </svg>
                    <input
                      type="text"
                      className="to-form__input"
                      placeholder="e.g. IB-98234-X"
                      value={orderId}
                      onChange={e => setOrderId(e.target.value)}
                    />
                  </div>
                </div>

                <div className="to-form__field">
                  <label className="to-form__label">Registered Mobile Number</label>
                  <div className="to-form__input-wrap">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.37a2 2 0 0 1 1.99-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <span className="to-form__prefix">+91</span>
                    <input
                      type="tel"
                      className="to-form__input"
                      placeholder="10-digit mobile number"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    />
                  </div>
                </div>

                {error && (
                  <div className="to-error">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    {error}
                  </div>
                )}

                <button type="submit" className="to-form__btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                  </svg>
                  Track My Order
                </button>
              </form>

              <div className="to-card__divider">
                <span>or</span>
              </div>

              <div className="to-card__links">
                <Link to="/account/orders" className="to-card__link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><path d="M16 3H8L6 7h12l-2-4z"/>
                  </svg>
                  View My Orders
                </Link>
                <Link to="/contact" className="to-card__link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  Contact Support
                </Link>
              </div>
            </div>

            {/* Trust strip */}
            <div className="to-trust">
              {[
                { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#81C862" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>, text: 'Real-time tracking' },
                { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ABCE8" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.37a2 2 0 0 1 1.99-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>, text: 'SMS & email alerts' },
                { icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E87722" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>, text: 'Pan-India delivery' },
              ].map((t, i) => (
                <div key={i} className="to-trust__item">
                  {t.icon}
                  <span>{t.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT: Tracking result ── */}
          <div className="to-right">
            {!tracked ? (
              <div className="to-empty">
                <div className="to-empty__icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.2">
                    <rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/>
                    <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                  </svg>
                </div>
                <h3 className="to-empty__title">No Tracking Info Yet</h3>
                <p className="to-empty__sub">Enter your order details on the left to see live tracking status.</p>
              </div>
            ) : (
              <div className="to-result">

                {/* Status card */}
                <div className="to-status-card">
                  <div className="to-status-card__top">
                    <div>
                      <p className="to-status-card__order-id">Order #{order.id}</p>
                      <div className="to-status-card__badge">{order.status}</div>
                    </div>
                    <div className="to-status-card__eta">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#81C862" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      <span>{order.estimatedDate}</span>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="to-progress">
                    <div className="to-progress__bar">
                      <div className="to-progress__fill" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="to-progress__label">{Math.round(progress)}% complete</span>
                  </div>
                </div>

                {/* Order summary */}
                <div className="to-order-info">
                  {[
                    { label: 'Product', val: order.product },
                    { label: 'Quantity', val: String(order.qty) },
                    { label: 'Total', val: order.total },
                    { label: 'Payment', val: order.payment },
                    { label: 'Deliver To', val: order.address },
                  ].map(row => (
                    <div key={row.label} className="to-order-info__row">
                      <span className="to-order-info__label">{row.label}</span>
                      <span className="to-order-info__val">{row.val}</span>
                    </div>
                  ))}
                </div>

                {/* Timeline */}
                <div className="to-timeline">
                  <h3 className="to-timeline__title">Shipment Timeline</h3>
                  {order.steps.map((step, i) => (
                    <div
                      key={i}
                      className={`to-step ${step.done ? 'to-step--done' : ''} ${step.active ? 'to-step--active' : ''}`}
                    >
                      <div className="to-step__indicator">
                        <div className="to-step__dot">
                          {step.done && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                          {step.active && <div className="to-step__pulse" />}
                        </div>
                        {i < order.steps.length - 1 && <div className="to-step__line" />}
                      </div>
                      <div className="to-step__content">
                        <p className="to-step__label">{step.label}</p>
                        <p className="to-step__detail">{step.detail}</p>
                        <p className="to-step__time">{step.time}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="to-actions">
                  <Link to="/account/orders" className="to-actions__btn to-actions__btn--outline">View All Orders</Link>
                  <Link to="/contact" className="to-actions__btn to-actions__btn--primary">Need Help?</Link>
                </div>

              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default TrackOrder;
